import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  IBusinessPhotosUploadProps,
  ImageStatus,
  PhotoSource,
  BusinessEditPhotoProps,
} from 'containers/OnboardingBusiness/types';
import ConfirmationModal from 'components/Common/ConfirmationModal';
import { IoMdClose } from 'react-icons/io';
import { useGetPreSignedUrlMutation } from 'store/apis/imageUploadApi';
import { toast } from 'react-hot-toast';
import Render from 'components/Common/Render';

const MAX_BANNER_IMAGE_SIZE = 20971520; // 20 MB in bytes
export const MAX_BANNER_IMAGE_COUNT = 5;

const defaultImageObject = {
  rawImage: '',
  photoUrl: '',
  status: ImageStatus.QUEUED,
  uuid: '',
  source: PhotoSource.BUSINESS,
};

const BusinessPhotosUpload: React.FC<IBusinessPhotosUploadProps> = ({
  uploadedFiles,
  setUploadedFiles,
  fileStatuses,
  setFileStatuses,
  isDragOver,
  setIsDragOver,
  handleFileSelect,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleFileInputChange,
  removeFile,
  retryUpload,
  previewFile,
}) => {
  const [queuedImages, setQueuedImages] = useState<BusinessEditPhotoProps[]>([]);
  const [getPreSignedUrl] = useGetPreSignedUrlMutation();

  // State management for preview
  const [showImagePreview, setShowImagePreview] = useState<boolean>(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');

  // State management for delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [imageToDelete, setImageToDelete] = useState<BusinessEditPhotoProps | null>(null);

  // State for drag and drop reordering
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Touch support for mobile
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [isDraggingMobile, setIsDraggingMobile] = useState<boolean>(false);
  const [currentTouchX, setCurrentTouchX] = useState<number | null>(null);
  const [currentTouchY, setCurrentTouchY] = useState<number | null>(null);
  const [dragStartElementRect, setDragStartElementRect] = useState<DOMRect | null>(null);
  const [draggedImageUuid, setDraggedImageUuid] = useState<string | null>(null);

  const uploadToS3 = async (image: BusinessEditPhotoProps, retry: boolean = false) => {
    let updatedImages: BusinessEditPhotoProps[] = [];
    try {
      // When we do retry we don't want new image to get created either we want to retry upload of same image
      if (retry) {
        updatedImages = uploadedFiles.map((item) => {
          if (image.uuid === item.uuid) {
            return { ...item, status: ImageStatus.UPLOADING };
          } else {
            return item;
          }
        });
      } else {
        updatedImages = [...uploadedFiles, { ...image, status: ImageStatus.UPLOADING }];
      }
      setUploadedFiles(updatedImages);

      const payload = retry && !!image.uuid ? { imageUuid: image.uuid } : {};
      // Request signed url from backend
      const data = await getPreSignedUrl(payload).unwrap();

      if (!!data) {
        const { fields: paramsFromRequestUrl, url, uuid } = data;
        const requestFormData = new FormData();

        // Append AWS S3 fields
        Object.keys(paramsFromRequestUrl).forEach((key: string) => {
          const requestParams = (paramsFromRequestUrl as any)[key];
          requestFormData.append(key, requestParams);
        });
        requestFormData.append('file', image.rawImage as File);

        const uploadedFileUrl = `${url}${paramsFromRequestUrl.key}`;

        // Direct upload to S3
        const result = await axios.post(url, requestFormData, {
          headers: {
            Authorization: '',
            Accept: 'application/json',
            'Content-Type': 'multipart/form-data',
          },
        });

        // Handle upload result
        if (result.status === 200 || result.status === 204) {
          const imagesToUpdate = updatedImages.map((i: BusinessEditPhotoProps) => {
            if (i.status === ImageStatus.UPLOADING) {
              return {
                ...i,
                photoUrl: uploadedFileUrl,
                status: ImageStatus.UPLOADED,
                rawImage: image.rawImage,
                uuid,
              };
            } else {
              return i;
            }
          });
          setUploadedFiles(imagesToUpdate);
        } else {
          const imagesToUpdate = updatedImages.map((i: BusinessEditPhotoProps) => {
            if (i.status === ImageStatus.UPLOADING) {
              return {
                ...i,
                photoUrl: uploadedFileUrl,
                status: ImageStatus.FAILED,
                rawImage: image.rawImage,
                uuid,
              };
            } else {
              return i;
            }
          });
          setUploadedFiles(imagesToUpdate);
        }
      } else {
        const imagesToUpdate = updatedImages.map((i: BusinessEditPhotoProps) => {
          if (i.status === ImageStatus.UPLOADING) {
            return {
              ...i,
              photoUrl: '',
              status: ImageStatus.FAILED,
              rawImage: image.rawImage,
              source: PhotoSource.COMMUNITY,
              uuid: '',
            };
          } else {
            return i;
          }
        });
        setUploadedFiles(imagesToUpdate);
      }
    } catch (error) {
      const imagesToUpdate = updatedImages.map((i: BusinessEditPhotoProps) => {
        if (i.status === ImageStatus.UPLOADING) {
          return {
            ...i,
            photoUrl: '',
            status: ImageStatus.FAILED,
            rawImage: image.rawImage,
            uuid: '',
          };
        } else {
          return i;
        }
      });
      setUploadedFiles(imagesToUpdate);
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const image = event.target.files[0];

      if (uploadedFiles.length >= MAX_BANNER_IMAGE_COUNT) {
        toast.error('You can upload up to 5 images');
        return;
      }

      if (image.size > MAX_BANNER_IMAGE_SIZE) {
        toast.error('Image cannot be greater than 20 MB');
        return;
      }

      // Create a new file with updated name if needed
      let processedImage = image;
      if (!image.name || image.name.toLowerCase().endsWith('.heic')) {
        const newName = `${image.name || `upload_image_${Date.now()}`}`;
        processedImage = new File([image], newName, { type: image.type });
      }

      setQueuedImages([
        ...queuedImages,
        {
          ...defaultImageObject,
          status: ImageStatus.QUEUED,
          rawImage: processedImage,
        },
      ]);
    }
  };

  const onUploadBannerClick = (e: React.MouseEvent) => {
    const isAnyImageUploading = uploadedFiles.filter((i) => i.status === ImageStatus.UPLOADING);

    if (uploadedFiles.length >= MAX_BANNER_IMAGE_COUNT) {
      toast.error('You can upload up to 5 images');
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    if (isAnyImageUploading.length) {
      toast.error('Please wait while the image upload is in progress');
      e.stopPropagation();
      e.preventDefault();
    }

    return true;
  };

  useEffect(() => {
    const isAnyImageUploading = uploadedFiles.filter((i) => i.status === ImageStatus.UPLOADING);
    if (queuedImages.length && !isAnyImageUploading.length) {
      setQueuedImages(queuedImages.slice(1));
      uploadToS3(queuedImages[0]);
    }
  }, [queuedImages]);

  const handleRetry = (image: BusinessEditPhotoProps) => {
    uploadToS3(image, true);
  };

  const handleDelete = (image: BusinessEditPhotoProps) => {
    setImageToDelete(image);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (imageToDelete) {
      const updatedImages = uploadedFiles.filter((i) => i.uuid !== imageToDelete.uuid);
      setUploadedFiles(updatedImages);
      setShowDeleteConfirm(false);
      setImageToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setImageToDelete(null);
  };

  // Close preview function
  const onCloseImagePreview = () => {
    setPreviewImageUrl('');
    setShowImagePreview(false);
  };

  // Open preview function
  const onClickCarousel = (image: BusinessEditPhotoProps) => {
    const imageUrl = image.photoUrl || URL.createObjectURL(image.rawImage as File);
    setPreviewImageUrl(imageUrl);
    setShowImagePreview(true);
  };

  // Drag and drop handlers for reordering
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';

    // Create a custom drag image that shows the actual image being dragged
    const target = e.currentTarget as HTMLElement;
    const imageCard = target.querySelector('img');

    if (imageCard && e.dataTransfer.setDragImage) {
      // Clone the image for drag preview
      const dragPreview = imageCard.cloneNode(true) as HTMLImageElement;
      dragPreview.style.width = '150px';
      dragPreview.style.height = 'auto';
      dragPreview.style.opacity = '0.8';
      dragPreview.style.borderRadius = '8px';
      dragPreview.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';

      // Add to DOM temporarily
      dragPreview.style.position = 'absolute';
      dragPreview.style.top = '-9999px';
      document.body.appendChild(dragPreview);

      // Set as drag image
      e.dataTransfer.setDragImage(dragPreview, 75, 50);

      // Remove after drag starts
      setTimeout(() => {
        document.body.removeChild(dragPreview);
      }, 0);
    }
  };

  const handleDragOverImage = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeaveImage = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIndex(null);
  };

  const handleDropImage = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder the images
    const newImages = [...uploadedFiles];
    const [draggedImage] = newImages.splice(draggedIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);

    setUploadedFiles(newImages);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEndImage = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Touch event handlers for mobile drag and drop
  const handleTouchStart = (e: React.TouchEvent, index: number, imageUuid: string) => {
    const touch = e.touches[0];
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();

    setTouchStartX(touch.clientX);
    setTouchStartY(touch.clientY);
    setCurrentTouchX(touch.clientX);
    setCurrentTouchY(touch.clientY);
    setDragStartElementRect(rect);
    setDraggedIndex(index);
    setDraggedImageUuid(imageUuid);
    setIsDraggingMobile(true);

    // Add haptic feedback if available
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (draggedIndex === null || touchStartX === null || touchStartY === null || !draggedImageUuid)
      return;

    e.preventDefault(); // Prevent scrolling while dragging
    const touch = e.touches[0];

    // Update current touch position for visual feedback
    setCurrentTouchX(touch.clientX);
    setCurrentTouchY(touch.clientY);

    // Get all image containers
    const allImageContainers = document.querySelectorAll('[data-image-index]');
    let closestIndex = -1;
    let closestDistance = Infinity;

    // Find the closest image center to the touch point
    allImageContainers.forEach((container) => {
      const rect = container.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      // Calculate distance from touch point to center of this image
      const distance = Math.sqrt(
        Math.pow(touch.clientX - centerX, 2) + Math.pow(touch.clientY - centerY, 2),
      );

      // Also check if touch is within reasonable bounds (not too far away)
      const isWithinVerticalBounds =
        touch.clientY >= rect.top - 50 && touch.clientY <= rect.bottom + 50;

      if (distance < closestDistance && isWithinVerticalBounds) {
        closestDistance = distance;
        closestIndex = parseInt(container.getAttribute('data-image-index') || '-1');
      }
    });

    if (closestIndex !== -1) {
      // Update drag over index for visual feedback
      setDragOverIndex(closestIndex);

      // Find current index of dragged image by UUID
      const currentDraggedIndex = uploadedFiles.findIndex((img) => img.uuid === draggedImageUuid);

      if (closestIndex !== currentDraggedIndex && currentDraggedIndex !== -1) {
        // Immediately reorder for visual feedback
        const newImages = [...uploadedFiles];
        const [draggedImage] = newImages.splice(currentDraggedIndex, 1);
        newImages.splice(closestIndex, 0, draggedImage);
        setUploadedFiles(newImages);

        // Update draggedIndex to the new position
        setDraggedIndex(closestIndex);

        // Add subtle haptic feedback during drag
        if (navigator.vibrate) {
          navigator.vibrate(5);
        }
      }
    } else {
      setDragOverIndex(null);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    setIsDraggingMobile(false);

    // Add haptic feedback for successful reorder
    if (draggedIndex !== null && navigator.vibrate) {
      navigator.vibrate([10, 50, 10]);
    }

    // Clean up state
    setDraggedIndex(null);
    setDragOverIndex(null);
    setTouchStartX(null);
    setTouchStartY(null);
    setCurrentTouchX(null);
    setCurrentTouchY(null);
    setDragStartElementRect(null);
    setDraggedImageUuid(null);
  };

  return (
    <div className="pb-4">
      <h2 className="font-poppins font-semibold text-lg leading-none text-[#4C4BC9] mb-4 sm:mb-6">
        Add Business Photos
      </h2>

      {/* File Upload Area - Always visible */}
      <div
        className={`rounded-lg p-6 sm:p-12 text-center mb-6 sm:mb-8 transition-colors ${
          isDragOver ? 'bg-blue-50' : 'bg-white'
        }`}
        style={{
          backgroundImage: isDragOver
            ? `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%235856D6' stroke-width='2' stroke-dasharray='12%2c 8' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`
            : `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23D1D5DB' stroke-width='2' stroke-dasharray='12%2c 8' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="file-upload"
          multiple
          accept="image/*"
          onChange={handleImageChange}
          className="hidden"
        />
        <label htmlFor="file-upload" className="cursor-pointer" onClick={onUploadBannerClick}>
          <div className="flex flex-col items-center space-y-3 sm:space-y-4">
            {/* Upload Icon */}
            <div className="flex items-center justify-center">
              <svg
                className="w-16 h-16 sm:w-20 sm:h-20"
                viewBox="0 0 60 60"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M52.425 2.5H45.075C41.9 2.5 40 4.4 40 7.575V14.925C40 18.1 41.9 20 45.075 20H52.425C55.6 20 57.5 18.1 57.5 14.925V7.575C57.5 4.4 55.6 2.5 52.425 2.5ZM52.975 10.775C52.675 11.075 52.275 11.225 51.875 11.225C51.475 11.225 51.075 11.075 50.775 10.775L50.325 10.325V15.925C50.325 16.8 49.625 17.5 48.75 17.5C47.875 17.5 47.175 16.8 47.175 15.925V10.325L46.725 10.775C46.125 11.375 45.125 11.375 44.525 10.775C43.925 10.175 43.925 9.175 44.525 8.575L47.65 5.45C47.775 5.325 47.95 5.225 48.125 5.15C48.175 5.125 48.225 5.125 48.275 5.1C48.4 5.05 48.525 5.025 48.675 5.025H48.825C49 5.025 49.15 5.05 49.325 5.125H49.375C49.55 5.2 49.7 5.3 49.825 5.425C49.85 5.45 49.85 5.45 49.875 5.45L53 8.575C53.6 9.175 53.6 10.175 52.975 10.775Z"
                  fill="#0102CE"
                />
                <path
                  d="M22.5003 25.9501C25.7865 25.9501 28.4502 23.2861 28.4502 20C28.4502 16.7139 25.7865 14.05 22.5003 14.05C19.2142 14.05 16.5503 16.7139 16.5503 20C16.5503 23.2861 19.2142 25.9501 22.5003 25.9501Z"
                  fill="#0102CE"
                />
                <path
                  d="M52.425 20H51.25V31.525L50.925 31.25C48.975 29.575 45.825 29.575 43.875 31.25L33.475 40.175C31.525 41.85 28.375 41.85 26.425 40.175L25.575 39.475C23.8 37.925 20.975 37.775 18.975 39.125L9.625 45.4C9.075 44 8.75 42.375 8.75 40.475V19.525C8.75 12.475 12.475 8.75 19.525 8.75H40V7.575C40 6.575 40.175 5.725 40.575 5H19.525C10.425 5 5 10.425 5 19.525V40.475C5 43.2 5.475 45.575 6.4 47.575C8.55 52.325 13.15 55 19.525 55H40.475C49.575 55 55 49.575 55 40.475V19.425C54.275 19.825 53.425 20 52.425 20Z"
                  fill="#0102CE"
                />
              </svg>
            </div>
            <div className="space-y-1 sm:space-y-3">
              <p className="text-gray-700 text-sm sm:text-base">
                Drag & Drop or <span className="text-[#5856D6] font-medium">Choose file</span> to
                upload
              </p>
              <p className="text-gray-500 text-xs sm:text-sm">
                You can upload maximum upto 5 photos
              </p>
            </div>
            <p className="text-gray-400 text-[11px] sm:text-xs italic leading-relaxed px-2 sm:px-0 mt-2 sm:mt-5">
              For the best result upload an image of landscape orientation with a size of 350 × 150
              px (Aspect ratio 2:1)
            </p>
          </div>
        </label>
      </div>

      {/* Uploaded Files Section - Only show if there are uploaded files */}
      <Render when={uploadedFiles.length > 0}>
        <div className="mb-6 sm:mb-8">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 font-poppins font-semibold text-[#6B46C1]">
            Uploaded photos
          </h3>
          <div className="flex flex-col sm:flex-row gap-4 sm:overflow-x-auto sm:pb-2">
            {uploadedFiles.map((image, index) => {
              const isUploading = image.status === ImageStatus.UPLOADING;
              const isFailed = image.status === ImageStatus.FAILED;
              const isQueued = image.status === ImageStatus.QUEUED;
              const isUploaded = image.status === ImageStatus.UPLOADED;

              const isDraggingThis = draggedImageUuid === image.uuid;
              const isDropTarget = dragOverIndex === index;

              // Calculate transform for finger-following effect on mobile
              let transformStyle = 'none';
              let positionStyle: React.CSSProperties = {};

              if (
                isDraggingThis &&
                currentTouchX !== null &&
                currentTouchY !== null &&
                touchStartX !== null &&
                touchStartY !== null &&
                dragStartElementRect
              ) {
                const deltaX = currentTouchX - touchStartX;
                const deltaY = currentTouchY - touchStartY;
                transformStyle = `translate(${deltaX}px, ${deltaY}px)`;
                positionStyle = {
                  position: 'relative',
                  pointerEvents: 'none', // Allow detecting elements underneath
                };
              }

              return (
                <div
                  key={image.uuid || index}
                  className="relative w-full sm:w-[240px] sm:flex-shrink-0"
                  data-image-index={index}
                  draggable={isUploaded}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOverImage(e, index)}
                  onDragLeave={handleDragLeaveImage}
                  onDrop={(e) => handleDropImage(e, index)}
                  onDragEnd={handleDragEndImage}
                  onTouchStart={(e) => isUploaded && handleTouchStart(e, index, image.uuid)}
                  onTouchMove={(e) => isUploaded && handleTouchMove(e)}
                  onTouchEnd={(e) => isUploaded && handleTouchEnd(e)}
                  style={{
                    cursor: isUploaded ? (isDraggingThis ? 'grabbing' : 'grab') : 'default',
                    opacity: 1,
                    transform: transformStyle,
                    touchAction: isUploaded ? 'none' : 'auto',
                    zIndex: isDraggingThis ? 1000 : 1,
                    WebkitTapHighlightColor: 'transparent',
                    ...positionStyle,
                  }}
                >
                  {/* Unified Card Container */}
                  <div
                    className="bg-white rounded-lg overflow-hidden border border-gray-200"
                    style={{
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                    }}
                  >
                    {/* Image Container */}
                    <div
                      className="relative w-full"
                      style={{
                        aspectRatio: '16/9',
                        WebkitTapHighlightColor: 'transparent',
                      }}
                    >
                      <img
                        src={image.photoUrl || URL.createObjectURL(image.rawImage as File)}
                        alt={`Upload ${index + 1}`}
                        className="w-full h-full object-cover select-none"
                        draggable={false}
                        style={{
                          WebkitTouchCallout: 'none',
                          WebkitUserSelect: 'none',
                          userSelect: 'none',
                        }}
                      />

                      {/* Failed Badge - Top Right Corner */}
                      <Render when={isFailed}>
                        <div className="absolute top-2 right-2 z-10">
                          <span className="bg-[#FF5630] text-white text-xs font-medium px-3 sm:px-2 py-1 rounded">
                            Failed
                          </span>
                        </div>
                      </Render>

                      {/* Status Overlay for Uploading/Queued */}
                      <Render when={isUploading || isQueued}>
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                          <div className="flex flex-col items-center space-y-2">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-white text-xs">
                              {isUploading ? 'Uploading...' : 'Queued...'}
                            </span>
                          </div>
                        </div>
                      </Render>
                    </div>

                    {/* Action Buttons */}
                    <div
                      className="flex justify-between items-center px-3 py-2 border-t border-gray-200 bg-[#FAFAFA] select-none"
                      style={{
                        WebkitUserSelect: 'none',
                      }}
                    >
                      <button
                        onClick={() => handleDelete(image)}
                        className="text-[#FF5630] sm:hover:text-red-800 text-sm sm:text-xs font-medium flex items-center gap-1 select-none transition-all"
                        style={{
                          WebkitTapHighlightColor: 'transparent',
                        }}
                      >
                        <svg
                          className="w-4 h-4 sm:w-3.5 sm:h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                        Delete
                      </button>

                      <Render
                        when={isFailed}
                        elseRender={
                          <button
                            onClick={() => onClickCarousel(image)}
                            className="text-[#5856D6] sm:hover:text-blue-800 text-sm sm:text-xs font-medium flex items-center gap-1 select-none transition-all"
                            style={{
                              WebkitTapHighlightColor: 'transparent',
                            }}
                          >
                            <svg
                              className="w-4 h-4 sm:w-3.5 sm:h-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                            Preview
                          </button>
                        }
                      >
                        <button
                          onClick={() => handleRetry(image)}
                          className="text-[#5856D6] sm:hover:text-blue-800 text-sm sm:text-xs font-medium flex items-center gap-1 select-none transition-all"
                          style={{
                            WebkitTapHighlightColor: 'transparent',
                          }}
                        >
                          <svg
                            className="w-4 h-4 sm:w-3.5 sm:h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                          </svg>
                          Try Again
                        </button>
                      </Render>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Render>

      {/* Image Preview Modal */}
      <Render when={showImagePreview}>
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-70 p-6"
          onClick={onCloseImagePreview}
        >
          <div
            className="relative max-w-[85vw] max-h-[85vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onCloseImagePreview}
              className="absolute -top-3 -right-3 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
            >
              <IoMdClose size={24} className="text-gray-700" />
            </button>
            <img
              src={previewImageUrl}
              alt="Preview"
              className="max-w-full max-h-[85vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
            />
          </div>
        </div>
      </Render>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteConfirm}
        onHide={cancelDelete}
        onConfirmClick={confirmDelete}
        yesButtonLabel="Yes, Delete"
        noButtonLabel="No, Cancel"
        renderHeader={
          <div className="flex items-center justify-between w-full">
            <h3 className="text-lg font-semibold text-gray-900">Are you sure?</h3>
            <button
              onClick={cancelDelete}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        }
        renderConfirmationMessage={
          <div className="text-center">
            <p className="text-gray-700">Deleting this image cannot be undone again</p>
          </div>
        }
        customClass="md:w-[400px] w-[320px]"
      />
    </div>
  );
};

export default BusinessPhotosUpload;
